/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import sample from 'lodash/sample';
import aiMusicGeneratorApi from 'services/api/aiMusicGenerator/aiMusicGeneratorApi';
import { BaseSong } from 'services/api/entities';
import songsApi from 'services/api/songs/songsApi';
import { DEFAULT_FORMULA_VALUES, NAMESPACE } from './constants';
import { getRecommendedBpmValue, getRecommendedInstruments } from './helpers';
import initialState from './initialState';
import {
  Formula,
  FormulaFields,
  FormulaFieldType,
  FormulaFieldValue,
  PromptType,
} from './types';

const aiSongGeneratorSlice = createSlice({
  name: NAMESPACE,
  initialState,
  reducers: {
    setPrimaryGenre(state, action: PayloadAction<{ primaryGenre: string }>) {
      state.primaryGenre = action.payload.primaryGenre;
      state.microGenre = null;

      state.formula = state.formula.map((field) => {
        switch (field.field) {
          case FormulaFields.Instruments:
            return {
              ...field,
              value: getRecommendedInstruments(state),
            };
          case FormulaFields.Bpm:
            return {
              ...field,
              value: getRecommendedBpmValue(state),
            };
          default:
            return field;
        }
      });
    },
    setMicroGenre(state, action: PayloadAction<{ microGenre: string | null }>) {
      const { microGenre } = action.payload;
      state.microGenre = microGenre !== state.microGenre ? microGenre : null;

      state.formula = state.formula.map((field) => {
        switch (field.field) {
          case FormulaFields.Instruments:
            return {
              ...field,
              value: getRecommendedInstruments(state),
            };
          case FormulaFields.Bpm:
            return {
              ...field,
              value: getRecommendedBpmValue(state),
            };
          default:
            return field;
        }
      });
    },
    setFormula(state, action: PayloadAction<{ formula: Formula }>) {
      state.formula = action.payload.formula;
    },

    addFormulaField(
      state,
      action: PayloadAction<{
        field: FormulaFieldType;
        value?: FormulaFieldValue;
      }>
    ) {
      const { field, value } = action.payload;

      if (field === FormulaFields.GenreBlend) {
        const randomGenre = sample(state.genres)?.name;
        if (!randomGenre) {
          return;
        }

        state.formula.push({
          field,
          value: randomGenre,
        });

        return;
      }

      if (field === FormulaFields.Structure) {
        state.formula.push({
          field,
          value: state.structures[0]?.id ?? null,
        });
        return;
      }

      if (field === FormulaFields.Bpm) {
        state.formula.push({
          field,
          value: getRecommendedBpmValue(state),
        });
        return;
      }

      if (field === FormulaFields.Instruments) {
        state.formula.push({
          field,
          value: getRecommendedInstruments(state),
        });
        return;
      }

      // @ts-ignore
      state.formula.push({
        field,
        // @ts-ignore
        value: value ?? DEFAULT_FORMULA_VALUES[field],
      });
    },
    deleteFormulaField(
      state,
      action: PayloadAction<{ field: FormulaFieldType }>
    ) {
      const { field } = action.payload;

      state.formula = state.formula.filter((item) => item.field !== field);
    },
    setFormulaFieldValue(
      state,
      action: PayloadAction<{
        field: FormulaFieldType;
        value: FormulaFieldValue;
      }>
    ) {
      const { field, value } = action.payload;

      // @ts-ignore
      state.formula = state.formula.map((item) => {
        if (item.field === field) {
          return {
            field,
            value,
          };
        }
        return item;
      });
    },
    setFormulaDrawerState(
      state,
      action: PayloadAction<{
        isOpen: boolean;
      }>
    ) {
      state.isFormulaDrawerOpened = action.payload.isOpen;
    },
    setCurrentTrack(
      state,
      action: PayloadAction<{
        id: BaseSong['id'];
      }>
    ) {
      state.currentTrack = action.payload.id;
    },
    resetCurrentTrack(state) {
      state.currentTrack = null;
    },
    setPromptType(state, action: PayloadAction<PromptType>) {
      state.promptType = action.payload;
    },

    setTextPrompt(state, action: PayloadAction<string>) {
      state.textPrompt = action.payload;
    },

    setPlaceholderPrompt(state, action: PayloadAction<string>) {
      state.placeholderPrompt = action.payload;
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(
      aiMusicGeneratorApi.endpoints.createBundle.matchPending,
      (state) => {
        state.isFormulaDrawerOpened = false;
      }
    );

    builder.addMatcher(
      songsApi.endpoints.addToLibrary.matchFulfilled,
      (
        state,
        {
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { songId } = originalArgs;
        const song = state.tracks[songId];
        if (!song) {
          return;
        }
        song.isInLibrary = true;
      }
    );

    builder.addMatcher(
      songsApi.endpoints.removeFromLibrary.matchFulfilled,
      (
        state,
        {
          meta: {
            arg: { originalArgs },
          },
        }
      ) => {
        const { songId } = originalArgs;
        const song = state.tracks[songId];
        if (!song) {
          return;
        }
        song.isInLibrary = false;
      }
    );

    builder.addMatcher(
      aiMusicGeneratorApi.endpoints.getAiMusicGeneratorGenres.matchFulfilled,
      (state, { payload: genres }) => {
        state.genres = genres;

        if (!state.primaryGenre) {
          state.primaryGenre = sample(genres)?.name ?? null;
        }
      }
    );
    builder.addMatcher(
      aiMusicGeneratorApi.endpoints.getAiMusicGeneratorSongStructures
        .matchFulfilled,
      (state, { payload: structures }) => {
        state.structures = structures;
        state.formula.forEach((item) => {
          if (item.field === FormulaFields.Structure && item.value === null) {
            item.value = structures[0].id;
          }
          return item;
        });
      }
    );
  },
});

export const { actions, reducer } = aiSongGeneratorSlice;
